import React, { DOMAttributes, FC, useMemo } from 'react';
import {
  defaultTooltipOffset,
  initialTooltipStyle,
  tooltipAnimationOptions,
} from 'eazychart-core';
import {
  AnimationOptions,
  NormalizedDatum,
  Point,
  ShapeDatum,
} from 'eazychart-core';
import { useAnimation } from 'eazychart-react';
import { useSnapshot } from 'hooks/useSnapshot';
import { usePageCarto } from 'hooks/usePageCarto';
import { GeoFeatureDatum } from 'interfaces/geojson';

export interface TooltipProps extends DOMAttributes<HTMLDivElement> {
  offset?: Point;
  datum: NormalizedDatum | null;
  shapeDatum: ShapeDatum | null;
  isVisible: boolean;
  mousePosition: Point;
  animationOptions?: AnimationOptions;
}

export const Tooltip: FC<TooltipProps> = ({
  offset = defaultTooltipOffset,
  datum,
  shapeDatum,
  isVisible,
  mousePosition,
  animationOptions = tooltipAnimationOptions,
  ...rest
}) => {
  const snapshot = useSnapshot();
  const { map } = usePageCarto();
  const targetStyle = useMemo(
    () => ({
      left: `${mousePosition.x ? mousePosition.x + offset.x : 0}px`,
      top: `${mousePosition.y ? mousePosition.y + offset.y : 0}px`,
      opacity: isVisible ? 1.0 : 0.0,
    }),
    [mousePosition, isVisible, offset]
  );

  const animatedStyle = useAnimation<any>(
    targetStyle,
    initialTooltipStyle,
    animationOptions,
    [offset]
  );

  const {
    id: _id,
    color: _color,
    label: _label,
    ...attributes
  } = datum || {
    color: undefined,
  };

  const featurePropertiesEntries = useMemo(() => {
    const featureProperties =
      (shapeDatum as GeoFeatureDatum | null | undefined)?.feature?.properties ||
      null;
    const geoMapProperties = map?.properties || [];

    if (!featureProperties || geoMapProperties.length === 0) {
      return [];
    }

    return geoMapProperties
      .filter(({ isGeoCode }) => !isGeoCode)
      .map(({ name }) => [
        name,
        (featureProperties as Record<string, unknown>)[name],
      ])
      .filter(([, value]) => value !== null && value !== undefined) as Array<
      [string, unknown]
    >;
  }, [map, shapeDatum]);

  const isCircle = shapeDatum && 'radius' in shapeDatum;
  const showAttribute = isCircle
    ? snapshot.bubbleDomainKey
    : snapshot.mapDomainKey;

  return datum ? (
    <div className="ez-tooltip" style={animatedStyle} {...rest}>
      {shapeDatum?.color && (
        <div
          className="ez-tooltip-color"
          style={{ backgroundColor: shapeDatum.color }}
        ></div>
      )}
      <div className="ez-tooltip-text">
        {featurePropertiesEntries.map(([attribute, value]) => (
          <div key={attribute} className={`ez-tooltip-attribute ${attribute}`}>
            <div className="ez-tooltip-attribute--name">{attribute} :</div>
            <div className="ez-tooltip-attribute--value">{String(value)}</div>
          </div>
        ))}
        {Object.keys(attributes)
          .filter((attribute) => attribute === showAttribute)
          .map((attribute) => {
            return (
              <div
                key={attribute}
                className={`ez-tooltip-attribute ${attribute}`}
              >
                <div className="ez-tooltip-attribute--name">{attribute} :</div>
                <div className="ez-tooltip-attribute--value">
                  {datum[attribute] as string}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  ) : null;
};
